import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  RotateCw, 
  Check, 
  X, 
  ChevronLeft, 
  ChevronRight,
  Sparkles,
  Trophy,
  Brain,
  Loader2
} from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FlashcardsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [flashcards, setFlashcards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionStats, setSessionStats] = useState({ correct: 0, incorrect: 0, total: 0 });
  const [sessionComplete, setSessionComplete] = useState(false);

  const courseId = user?.selected_course || 'pt-en';

  const fetchFlashcards = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/flashcards/review/${courseId}?limit=20`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setFlashcards(data.flashcards || []);
        setSessionStats(prev => ({ ...prev, total: data.flashcards?.length || 0 }));
      }
    } catch (error) {
      console.error('Error fetching flashcards:', error);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchFlashcards();
  }, [fetchFlashcards]);

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleResponse = async (quality) => {
    const currentCard = flashcards[currentIndex];
    
    // Update local stats
    if (quality >= 3) {
      setSessionStats(prev => ({ ...prev, correct: prev.correct + 1 }));
    } else {
      setSessionStats(prev => ({ ...prev, incorrect: prev.incorrect + 1 }));
    }

    // Submit review to backend
    try {
      await fetch(`${API_URL}/api/flashcards/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          flashcardId: `${currentCard.lessonId}_${currentCard.front}`,
          courseId: courseId,
          quality: quality
        })
      });
    } catch (error) {
      console.error('Error submitting review:', error);
    }

    // Move to next card
    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setIsFlipped(false);
    } else {
      setSessionComplete(true);
    }
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setSessionStats({ correct: 0, incorrect: 0, total: flashcards.length });
    setSessionComplete(false);
    fetchFlashcards();
  };

  if (loading) {
    return (
      <Layout title="Flashcards" showBack>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (flashcards.length === 0) {
    return (
      <Layout title="Flashcards" showBack>
        <div className="px-4 py-8 text-center space-y-4">
          <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto">
            <Trophy className="w-10 h-10 text-success" />
          </div>
          <h2 className="text-xl font-bold">Parabéns!</h2>
          <p className="text-muted-foreground">
            Você revisou todos os flashcards por hoje. Volte amanhã para mais!
          </p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Voltar ao Início
          </Button>
        </div>
      </Layout>
    );
  }

  if (sessionComplete) {
    const accuracy = Math.round((sessionStats.correct / sessionStats.total) * 100);
    
    return (
      <Layout title="Flashcards" showBack>
        <div className="px-4 py-8 text-center space-y-6">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto">
            <Sparkles className="w-12 h-12 text-white" />
          </div>
          
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">Sessão Completa!</h2>
            <p className="text-muted-foreground">
              Você revisou {sessionStats.total} cartões
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
            <div className="p-4 rounded-xl bg-success/10 text-center">
              <p className="text-2xl font-bold text-success">{sessionStats.correct}</p>
              <p className="text-xs text-muted-foreground">Corretos</p>
            </div>
            <div className="p-4 rounded-xl bg-destructive/10 text-center">
              <p className="text-2xl font-bold text-destructive">{sessionStats.incorrect}</p>
              <p className="text-xs text-muted-foreground">Errados</p>
            </div>
            <div className="p-4 rounded-xl bg-primary/10 text-center">
              <p className="text-2xl font-bold text-primary">{accuracy}%</p>
              <p className="text-xs text-muted-foreground">Precisão</p>
            </div>
          </div>

          <div className="flex gap-3 justify-center pt-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Início
            </Button>
            <Button onClick={handleRestart} className="btn-primary-gradient">
              <RotateCw className="w-4 h-4 mr-1" />
              Revisar Mais
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const currentCard = flashcards[currentIndex];
  const progress = ((currentIndex + 1) / flashcards.length) * 100;

  return (
    <Layout title="Flashcards" showBack>
      <div className="px-4 py-6 space-y-6">
        {/* Progress Header */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-primary" />
              Cartão {currentIndex + 1} de {flashcards.length}
            </span>
            <span className="text-muted-foreground">
              {sessionStats.correct} ✓ / {sessionStats.incorrect} ✗
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Flashcard */}
        <div 
          className="relative w-full max-w-md mx-auto perspective-1000"
          style={{ minHeight: '320px' }}
        >
          <div
            onClick={handleFlip}
            className={cn(
              "absolute inset-0 cursor-pointer transition-transform duration-500 transform-style-3d",
              isFlipped && "rotate-y-180"
            )}
            style={{
              transformStyle: 'preserve-3d',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
            }}
          >
            {/* Front of card */}
            <div 
              className="absolute inset-0 backface-hidden rounded-2xl bg-gradient-to-br from-card to-card/80 border border-border shadow-xl p-8 flex flex-col items-center justify-center"
              style={{ backfaceVisibility: 'hidden' }}
            >
              <p className="text-sm text-muted-foreground mb-4">Toque para virar</p>
              <p className="text-3xl font-bold text-center">{currentCard?.front}</p>
              {currentCard?.reading && (
                <p className="text-lg text-muted-foreground mt-2">{currentCard.reading}</p>
              )}
            </div>
            
            {/* Back of card */}
            <div 
              className="absolute inset-0 backface-hidden rounded-2xl bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 shadow-xl p-8 flex flex-col items-center justify-center"
              style={{ 
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)'
              }}
            >
              <p className="text-sm text-muted-foreground mb-4">Tradução</p>
              <p className="text-3xl font-bold text-center text-primary">{currentCard?.back}</p>
            </div>
          </div>
        </div>

        {/* Response Buttons */}
        <div className="space-y-3 max-w-md mx-auto">
          <p className="text-center text-sm text-muted-foreground">
            {isFlipped ? 'Como foi?' : 'Toque no cartão para ver a resposta'}
          </p>
          
          {isFlipped && (
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                size="lg"
                onClick={() => handleResponse(1)}
                className="border-destructive/30 hover:bg-destructive/10 hover:border-destructive"
              >
                <X className="w-5 h-5 mr-2 text-destructive" />
                Não sei
              </Button>
              <Button
                size="lg"
                onClick={() => handleResponse(4)}
                className="bg-success hover:bg-success/90"
              >
                <Check className="w-5 h-5 mr-2" />
                Sei
              </Button>
            </div>
          )}
        </div>

        {/* Navigation hint */}
        <div className="flex justify-center gap-8 text-muted-foreground text-sm">
          <button 
            onClick={() => currentIndex > 0 && setCurrentIndex(prev => prev - 1)}
            disabled={currentIndex === 0}
            className="flex items-center gap-1 disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </button>
          <button 
            onClick={() => currentIndex < flashcards.length - 1 && setCurrentIndex(prev => prev + 1)}
            disabled={currentIndex === flashcards.length - 1}
            className="flex items-center gap-1 disabled:opacity-30"
          >
            Próximo
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default FlashcardsPage;
